import { useEffect, useMemo, useState } from 'react';
import axios from '../api';

export default function AdminUploads() {
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [newCat, setNewCat] = useState({ name: '', description: '', isPublicView: false, isPublicDownload: false });
  const [editCat, setEditCat] = useState(null);
  const [permUserId, setPermUserId] = useState('');
  const [permRole, setPermRole] = useState('');
  const [permList, setPermList] = useState([]);
  const [permCanView, setPermCanView] = useState(true);
  const [permCanDownload, setPermCanDownload] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);

  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // 0..100
  const [uploadError, setUploadError] = useState('');
  const [uploadOk, setUploadOk] = useState('');
  const [thumbBusyFileId, setThumbBusyFileId] = useState(null);

  const getErrMsg = (err, fallback) => {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const msg = data?.error || data?.message || err?.message;
    if (status === 413) {
      const maxMb = data?.maxMb;
      return maxMb ? `Datei zu groß (max. ${maxMb} MB).` : 'Datei zu groß (Request zu groß).';
    }
    if (status && msg) return `${status}: ${msg}`;
    return msg || fallback || 'Unbekannter Fehler';
  };

  function loadCats() {
    axios.get('/uploads/admin/categories').then(res => setCategories(res.data.items || [])).catch(()=>{});
  }
  function loadFiles(catId) {
    const qs = catId ? `?categoryId=${catId}` : '';
    axios.get(`/uploads/admin/files${qs}`).then(res => setFiles(res.data.items || [])).catch(()=>{});
  }
  function loadPerms(catId) {
    if (!catId) return setPermList([]);
    axios.get(`/uploads/admin/categories/${catId}/permissions`).then(res => setPermList(res.data.items || [])).catch(()=>{});
  }

  useEffect(() => { loadCats(); }, []);
  useEffect(() => {
    // Load roles and users for selectors
    axios.get('/roles').then(res => setRoles(res.data || [])).catch(()=>{});
    axios.get('/users/list').then(res => setUsers(res.data.items || [])).catch(()=>{});
  }, []);
  useEffect(() => { loadFiles(selectedCat); loadPerms(selectedCat); }, [selectedCat]);

  const onCreateCat = async (e) => {
    e.preventDefault();
    await axios.post('/uploads/admin/categories', newCat);
    setNewCat({ name: '', description: '', isPublicView: false, isPublicDownload: false });
    loadCats();
  };

  const onUpdateCat = async (e) => {
    e.preventDefault();
    if (!editCat) return;
    await axios.put(`/uploads/admin/categories/${editCat.id}`, editCat);
    setEditCat(null);
    loadCats();
  };

  const onDeleteCat = async (id) => {
    if (!confirm('Kategorie inkl. Dateien löschen?')) return;
    await axios.delete(`/uploads/admin/categories/${id}`);
    if (String(selectedCat) === String(id)) setSelectedCat('');
    loadCats();
    loadFiles('');
    setPermList([]);
  };

  const onSavePerm = async () => {
    if (!selectedCat) return;
    if (!permUserId && !permRole) return;
    const base = { canView: !!permCanView, canDownload: !!permCanDownload };
    const payload = permUserId ? { ...base, userId: permUserId } : { ...base, role: permRole };
    try {
      await axios.post(`/uploads/admin/categories/${selectedCat}/permissions`, payload);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Fehler beim Speichern der Berechtigung';
      alert(`Speichern fehlgeschlagen: ${msg}`);
      throw err;
    }
    setPermUserId('');
    setPermRole('');
    setPermCanView(true);
    setPermCanDownload(false);
    loadPerms(selectedCat);
  };

  const onUpdatePerm = async (perm, changes) => {
    const payload = {
      canView: 'canView' in changes ? !!changes.canView : !!perm.canView,
      canDownload: 'canDownload' in changes ? !!changes.canDownload : !!perm.canDownload,
    };
    if (perm.userId) payload.userId = perm.userId; else payload.role = perm.role;
    await axios.post(`/uploads/admin/categories/${selectedCat}/permissions`, payload);
    loadPerms(selectedCat);
  };

  const onDeletePerm = async (userId) => {
    await axios.delete(`/uploads/admin/categories/${selectedCat}/permissions/${userId}`);
    loadPerms(selectedCat);
  };

  const onUpload = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    setUploadError('');
    setUploadOk('');
    setUploadProgress(null);

    const fd = new FormData(form);
    const file = fd.get('file');
    if (!file || (file instanceof File && file.size === 0)) {
      setUploadError('Bitte eine Datei auswählen.');
      return;
    }

    setUploadBusy(true);
    try {
      await axios.post('/uploads/admin/files', fd, {
        // Do NOT set Content-Type manually; axios/browser will set boundary.
        onUploadProgress: (ev) => {
          const total = ev?.total;
          const loaded = ev?.loaded;
          if (typeof total === 'number' && total > 0 && typeof loaded === 'number') {
            const p = Math.round((loaded / total) * 100);
            setUploadProgress(Math.max(0, Math.min(100, p)));
          }
        },
      });
      form.reset();
      setUploadOk('Upload erfolgreich.');
      loadFiles(selectedCat);
    } catch (err) {
      setUploadError(getErrMsg(err, 'Upload fehlgeschlagen'));
    } finally {
      setUploadBusy(false);
      setUploadProgress(null);
    }
  };

  const onChangeFileCategory = async (fileId, newCatId) => {
    await axios.put(`/uploads/admin/files/${fileId}`, { categoryId: newCatId });
    loadFiles(selectedCat);
  };

  const openLogs = async (file) => {
    setLogsFor(file);
    const res = await axios.get(`/uploads/admin/files/${file.id}/logs`);
    setLogs(res.data.items || []);
  };
  const closeLogs = () => { setLogsFor(null); setLogs([]); };

  const getCatName = (id) => {
    const c = categories.find(x => String(x.id) === String(id));
    return c ? c.name : (id != null ? `#${id}` : '-');
  };

  const DetailView = ({ detail, action }) => {
    if (!detail) return null;
    let obj = null;
    if (typeof detail === 'string') {
      try { obj = JSON.parse(detail); } catch { /* leave null */ }
    }
    const boxStyle = {
      background: '#f6f8fa',
      color: '#111',
      border: '1px solid #e1e4e8',
      borderRadius: 6,
      padding: 8,
      marginTop: 6,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 12,
      overflowX: 'auto'
    };
    if (obj && typeof obj === 'object') {
      // Optional: small humanized summary if available
      const hasMove = obj.fromCategoryId != null || obj.toCategoryId != null;
      return (
        <div>
          {hasMove && (
            <div style={{ color: '#333', marginTop: 4 }}>
              Kategorie: <b>{getCatName(obj.fromCategoryId)}</b> → <b>{getCatName(obj.toCategoryId)}</b>
            </div>
          )}
          {obj.title != null && <div style={{ color: '#333' }}>Titel: <b>{String(obj.title)}</b></div>}
          {obj.description != null && <div style={{ color: '#333' }}>Beschreibung: <b>{String(obj.description)}</b></div>}
          <pre style={boxStyle}><code>{JSON.stringify(obj, null, 2)}</code></pre>
        </div>
      );
    }
    // Fallback plain string
    return <pre style={boxStyle}><code>{String(detail)}</code></pre>;
  };

  return (
    <div className="home-page">
      {/* Hero header */}
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Uploads & Kategorien</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign:'center', maxWidth:760 }}>Dateikategorien, Berechtigungen und Dateien verwalten.</p>
      </section>
      {/* Centered content */}
      <section style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px 40px 16px', color: '#fff' }}>
        {/* Intro/Help box */}
        <div style={{
          background:'rgba(255,255,255,0.03)',
          border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:10,
          padding:'14px 16px',
          margin:'0 0 18px 0',
          lineHeight:1.55,
          color:'#ddd'
        }}>
          <p style={{ margin:'0 0 8px 0' }}>
            Verwalte hier Dateikategorien, Berechtigungen und deine Uploads. Dateien können optional ein Thumbnail erhalten.
          </p>
          <ul style={{ margin:'0 0 6px 18px', padding:0 }}>
            <li><b>Kategorien</b> strukturieren deine Dateien. „Öffentlich sichtbar“ blendet die Kategorie für alle ein. Ohne dieses Häkchen ist Sichtbarkeit nur per Berechtigung möglich.</li>
            <li><b>Berechtigungen</b> kannst du pro <b>Nutzer</b> oder <b>Rolle</b> setzen (View = sichtbar, Download = herunterladen erlaubt).</li>
            <li><b>Uploads</b>: Wähle Kategorie, Titel, Datei und optional ein Thumbnail. Für Bilder kann ein Thumbnail automatisch erzeugt werden.</li>
          </ul>
          <p style={{ margin:0, color:'#ccc' }}>
            Tipp: Du kannst die Kategorie einer Datei später direkt in der Liste über das Dropdown ändern, Titel/Beschreibung bearbeiten oder das Thumbnail ersetzen/entfernen.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 16 }}>
          <div>
          <h3 style={{ margin:'8px 0' }}>Kategorien</h3>
          <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
          <ul style={{ listStyle:'none', margin:0, padding:0 }}>
            {categories.map(c => (
              <li key={c.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <button onClick={()=>setSelectedCat(c.id)} title="Kategorie auswählen"
                          style={{ marginRight: 8, fontWeight: selectedCat===c.id? 'bold':'normal' }}>{c.name}</button>
                  <span style={{ fontSize:12, color:'#aaa' }}>{c.description}</span>
                </div>
                <div>
                  <button onClick={()=>setEditCat({ ...c })}>Bearbeiten</button>
                  <button onClick={()=>onDeleteCat(c.id)} style={{ marginLeft: 6, color: 'red' }}>Löschen</button>
                </div>
              </li>
            ))}
          </ul>
          </div>

          <form onSubmit={onCreateCat} style={{ marginTop: 12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'12px' }}>
            <h4 style={{ marginTop:0 }}>Neue Kategorie</h4>
            <div style={{ display:'grid', gap:10 }}>
              <label>
                <div style={{ fontSize:12, color:'#bbb' }}>Name</div>
                <input placeholder="z. B. Grafiken" value={newCat.name} onChange={e=>setNewCat({...newCat, name: e.target.value})} required />
              </label>
              <label>
                <div style={{ fontSize:12, color:'#bbb' }}>Beschreibung (optional)</div>
                <textarea placeholder="Kurze Erläuterung der Inhalte" value={newCat.description} onChange={e=>setNewCat({...newCat, description: e.target.value})} />
              </label>
              <div>
                <label title="Wenn aktiv, sehen alle diese Kategorie in der Downloads-Seite."><input type="checkbox" checked={newCat.isPublicView} onChange={e=>setNewCat({...newCat, isPublicView: e.target.checked})} /> Öffentlich sichtbar</label>
                <label style={{ marginLeft: 12 }} title="Wenn aktiv, dürfen alle ohne weitere Berechtigung herunterladen."><input type="checkbox" checked={newCat.isPublicDownload} onChange={e=>setNewCat({...newCat, isPublicDownload: e.target.checked})} /> Öffentlicher Download</label>
              </div>
              <div><button type="submit">Anlegen</button></div>
            </div>
          </form>
          {editCat && (
            <form onSubmit={onUpdateCat} style={{ marginTop: 12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'12px' }}>
              <h4 style={{ marginTop:0 }}>Kategorie bearbeiten</h4>
              <div style={{ display:'grid', gap:10 }}>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Name</div>
                  <input placeholder="Name" value={editCat.name} onChange={e=>setEditCat({...editCat, name: e.target.value})} required />
                </label>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Beschreibung</div>
                  <textarea placeholder="Beschreibung" value={editCat.description||''} onChange={e=>setEditCat({...editCat, description: e.target.value})} />
                </label>
                <div>
                  <label><input type="checkbox" checked={!!editCat.isPublicView} onChange={e=>setEditCat({...editCat, isPublicView: e.target.checked})} /> Öffentlich sichtbar</label>
                  <label style={{ marginLeft: 12 }}><input type="checkbox" checked={!!editCat.isPublicDownload} onChange={e=>setEditCat({...editCat, isPublicDownload: e.target.checked})} /> Öffentlicher Download</label>
                </div>
                <div><button type="submit">Speichern</button> <button type="button" onClick={()=>setEditCat(null)}>Abbrechen</button></div>
              </div>
            </form>
          )}
          <div style={{ marginTop: 16, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'12px' }}>
            <h4 style={{ marginTop:0 }}>Berechtigungen (Nutzer & Rollen)</h4>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, color:'#bbb' }}>Kategorie:</span>
              <select value={selectedCat} onChange={e=>setSelectedCat(e.target.value)}>
                <option value="">— bitte wählen —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div style={{ fontSize:12, color:'#bbb', marginBottom:8 }}>
              Wähle entweder einen Nutzer <b>oder</b> eine Rolle. „Sichtbar (View)“ steuert die Anzeige der Kategorie in der Downloads-Seite. „Download“ erlaubt das Herunterladen der Dateien.
            </div>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr auto auto auto', alignItems: 'center' }}>
              <select value={permUserId} onChange={e=>{ setPermUserId(e.target.value); setPermRole(''); }} disabled={!selectedCat}>
                <option value="">— Nutzer wählen —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.displayName || u.username} (#{u.id})</option>
                ))}
              </select>
              <select value={permRole} onChange={e=>{ setPermRole(e.target.value); setPermUserId(''); }} disabled={!selectedCat}>
                <option value="">— Rolle wählen —</option>
                {roles.map(r => (
                  <option key={r.name} value={r.name}>{r.label || r.name}</option>
                ))}
              </select>
              <label style={{ whiteSpace: 'nowrap' }} title="Sichtbarkeit der Kategorie in der Downloads-Seite"><input type="checkbox" checked={permCanView} onChange={e=>setPermCanView(e.target.checked)} disabled={!selectedCat} /> Sichtbar (View)</label>
              <label style={{ whiteSpace: 'nowrap' }} title="Erlaubt das Herunterladen der Dateien"><input type="checkbox" checked={permCanDownload} onChange={e=>setPermCanDownload(e.target.checked)} disabled={!selectedCat} /> Download</label>
              <button onClick={onSavePerm} disabled={!selectedCat || (!permUserId && !permRole)}>Speichern/Anlegen</button>
            </div>
            {!selectedCat && (
              <div style={{ fontSize:12, color:'#aaa', marginTop:8 }}>Bitte zuerst eine Kategorie auswählen, um Berechtigungen zu bearbeiten.</div>
            )}
            {selectedCat && (
              <ul style={{ marginTop:8 }}>
                {permList.map(p => (
                  <li key={p.id} style={{ padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                    {p.userId ? (
                      <>User {(() => { const u = users.find(x => String(x.id)===String(p.userId)); return u ? (u.displayName || u.username) + ` (#${u.id})` : `#${p.userId}`; })()}</>
                    ) : (
                      <>Rolle “{(() => { const r = roles.find(x => x.name===p.role); return r ? (r.label || r.name) : p.role; })()}”</>
                    )}
                    {' '}— View: <b>{String(p.canView)}</b> | DL: <b>{String(p.canDownload)}</b>
                    <button onClick={()=>onUpdatePerm(p, { canView: !p.canView })} style={{ marginLeft: 8 }}>View toggeln</button>
                    <button onClick={()=>onUpdatePerm(p, { canDownload: !p.canDownload })} style={{ marginLeft: 6 }}>DL toggeln</button>
                    <button onClick={()=>onDeletePerm(p.userId ? p.userId : p.role)} style={{ marginLeft: 6 }}>Entfernen</button>
                  </li>
                ))}
                {permList.length === 0 && (
                  <li style={{ color:'#aaa', fontSize:12 }}>Noch keine Berechtigungen für diese Kategorie.</li>
                )}
              </ul>
            )}
          </div>
        </div>
        <div>
          <h3 style={{ margin:'8px 0' }}>Dateien</h3>
          <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
            <div style={{ marginBottom: 8 }}>
              Kategorie-Filter: <select value={selectedCat} onChange={e=>setSelectedCat(e.target.value)}>
              <option value="">— alle —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
              <button onClick={()=>setSelectedCat('')} style={{ marginLeft:8 }}>Filter zurücksetzen</button>
            </div>
            <form onSubmit={onUpload}>
              <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr', gap:10 }}>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Kategorie</div>
                  <select name="categoryId" defaultValue="" required>
                    <option value="" disabled>Bitte wählen</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Titel</div>
                  <input name="title" placeholder="Sprechender Titel" required />
                </label>
                <label style={{ gridColumn:'1 / span 2' }}>
                  <div style={{ fontSize:12, color:'#bbb' }}>Beschreibung (optional)</div>
                  <input name="description" placeholder="Kurze Beschreibung der Datei" />
                </label>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Datei</div>
                  <input type="file" name="file" required />
                </label>
                <label>
                  <div style={{ fontSize:12, color:'#bbb' }}>Thumbnail (optional)</div>
                  <input type="file" name="thumbnail" accept="image/*" title="Optionales Thumbnail" />
                </label>
                <div style={{ gridColumn:'1 / span 2' }}>
                  <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:12, color:'#ccc' }} title="Für Bilddateien automatisch ein Vorschau-Thumbnail erzeugen.">
                    <input type="checkbox" name="autoThumb" defaultChecked /> Auto-Thumbnail (nur für Bilder)
                  </label>
                </div>
                <div style={{ gridColumn:'1 / span 2' }}>
                  <button type="submit" disabled={uploadBusy}>
                    {uploadBusy ? 'Upload…' : 'Upload'}
                  </button>
                  {uploadProgress != null && (
                    <span style={{ marginLeft: 10, fontSize: 12, color: '#bbb' }}>
                      {uploadProgress}%
                    </span>
                  )}
                  {uploadError && (
                    <div style={{ marginTop: 8, color: '#ffb4b4', fontSize: 12 }}>
                      {uploadError}
                    </div>
                  )}
                  {uploadOk && !uploadError && (
                    <div style={{ marginTop: 8, color: '#b7ffb7', fontSize: 12 }}>
                      {uploadOk}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
          <ul style={{ marginTop: 12 }}>
            {files.map(f => (
              <li key={f.id} style={{
                background:'rgba(255,255,255,0.03)',
                border:'1px solid rgba(255,255,255,0.08)',
                borderRadius:10,
                padding:'10px 12px',
                marginBottom:10
              }}>
                <div style={{ display:'flex', gap:12 }}>
                  {f.thumbnailFilename && (
                    <img src={`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/uploads/files/${f.thumbnailFilename}`} alt="thumb" style={{ width:68, height:68, objectFit:'cover', borderRadius:6, border:'1px solid rgba(255,255,255,0.08)' }} />
                  )}
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'baseline', gap:10, flexWrap:'wrap' }}>
                      <b style={{ fontSize:16 }}>{f.title}</b>
                      <span style={{ fontSize:12, color:'#bbb' }}>{(f.fileSize/1024).toFixed(1)} KB · {f.fileType}</span>
                    </div>
                    {f.description && <div style={{ fontSize: 12, color: '#ccc', marginTop:4 }}>{f.description}</div>}
                    <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
                      <label>
                        <span style={{ fontSize:12, color:'#bbb', marginRight:6 }}>Kategorie</span>
                        <select value={String(f.categoryId)} onChange={e=>onChangeFileCategory(f.id, e.target.value)}>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </label>
                      <button onClick={async()=>{ const title = prompt('Neuer Titel', f.title); if(title!=null){ await axios.put(`/uploads/admin/files/${f.id}`, { title }); loadFiles(selectedCat); } }}>Titel</button>
                      <button onClick={async()=>{ const desc = prompt('Neue Beschreibung', f.description||''); if(desc!=null){ await axios.put(`/uploads/admin/files/${f.id}`, { description: desc }); loadFiles(selectedCat); } }}>Beschreibung</button>
                      <button disabled={thumbBusyFileId === f.id} onClick={async()=>{
                        const inp = document.createElement('input');
                        inp.type = 'file';
                        inp.accept = 'image/*';
                        inp.onchange = async () => {
                          if (!inp.files || !inp.files[0]) return;
                          const fd = new FormData();
                          fd.append('thumbnail', inp.files[0]);
                          setUploadError('');
                          setUploadOk('');
                          setThumbBusyFileId(f.id);
                          try {
                            await axios.put(`/uploads/admin/files/${f.id}`, fd, {
                              // Do NOT set Content-Type manually; axios/browser will set boundary.
                              onUploadProgress: (ev) => {
                                const total = ev?.total;
                                const loaded = ev?.loaded;
                                if (typeof total === 'number' && total > 0 && typeof loaded === 'number') {
                                  const p = Math.round((loaded / total) * 100);
                                  setUploadProgress(Math.max(0, Math.min(100, p)));
                                }
                              },
                            });
                            setUploadOk('Thumbnail aktualisiert.');
                            loadFiles(selectedCat);
                          } catch (err) {
                            setUploadError(getErrMsg(err, 'Thumbnail-Upload fehlgeschlagen'));
                          } finally {
                            setThumbBusyFileId(null);
                            setUploadProgress(null);
                          }
                        };
                        inp.click();
                      }}>Thumbnail ersetzen</button>
                      {f.thumbnailFilename && (
                        <button onClick={async()=>{ await axios.put(`/uploads/admin/files/${f.id}`, { clearThumbnail: true }); loadFiles(selectedCat); }}>Thumbnail entfernen</button>
                      )}
                      <button onClick={()=>openLogs(f)}>Logs</button>
                      <button onClick={async()=>{ if(!confirm('Datei löschen?')) return; await axios.delete(`/uploads/admin/files/${f.id}`); loadFiles(selectedCat); }} style={{ color: 'red' }}>Löschen</button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {/* Close grid container */}
        </div>
      </section>

      {logsFor && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closeLogs}>
          <div style={{ background: '#fff', color:'#111', padding: 16, borderRadius: 8, width: '720px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }} onClick={e=>e.stopPropagation()}>
            <h3>Logs für: {logsFor.title} (#{logsFor.id})</h3>
            <ul>
              {logs.map(l => (
                <li key={l.id}>
                  <b>{l.action}</b> — {new Date(l.createdAt).toLocaleString()} — User #{l.userId || '-'}
                  <DetailView detail={l.detail} action={l.action} />
                </li>
              ))}
              {logs.length === 0 && <li>Keine Einträge.</li>}
            </ul>
            <div style={{ textAlign: 'right' }}>
              <button onClick={closeLogs}>Schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
